/**
 * VehicleEquipmentTab - Aba "Equipamentos" da ficha do veículo
 *
 * Lista TODOS os kits/rastreadores/chips instalados (um veículo pode ter
 * vários) e o histórico de instalações e retiradas. Fonte:
 * `get_vehicle_installed_equipment`.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-18 v1.4.0 - Histórico ordenado por sortMovementsForDisplay
 *            (empate de horário: instalação por cima), igual à aba da O.S.
 *   RELEASE: 2026-10-10 v1.3.0 - Histórico ganha a primeira coluna com seta de
 *            direção (verde p/ cima = entrada no veículo; vermelha p/ baixo =
 *            saída), via showDirection da StockMovementsTable.
 *   RELEASE: 2026-10-09 v1.1.0 - Histórico reutiliza a tabela oficial
 *            `StockMovementsTable` (mesma de Kits/Rastreadores/Chips e da OS),
 *            com total + botão atualizar; cards de instalados com visual premium.
 *   RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 4 do Estoque).
 */
import { useState } from 'react';
import { CalendarClock, Cpu, ExternalLink, Package, RefreshCw } from 'lucide-react';
import { ServiceOrderDetailsModal } from '@/components/service-orders/ServiceOrderDetailsModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { stockStatusLabel, stockStatusTone } from '@/hooks/useStockItems';
import { useVehicleEquipment } from '@/hooks/useStockMovements';
import { StockMovementsTable } from '@/components/stock/StockMovementsTable';
import { stockProductTypeBadgeClass } from '@/lib/stock-presentation';
import { cn } from '@/lib/utils';
import { sortMovementsForDisplay } from '@/lib/stockMovementDisplayOrder';

const KIND = { kit: 'Kit', tracker: 'Rastreador', chip: 'Chip' } as const;
const fmt = (v: string) => new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/** Mini-bloco rotulado usado nos cards de equipamento instalado. */
function InfoCell({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="min-w-0 rounded-md bg-muted/50 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="truncate text-xs font-medium tabular-nums text-foreground" title={value}>{value}</div>
    </div>
  );
}

export function VehicleEquipmentTab({ vehicleId }: { vehicleId: string }) {
  const { data, isLoading, isFetching, error, refetch } = useVehicleEquipment(vehicleId);
  // OS aberta a partir do card (modal por cima do veículo)
  const [osId, setOsId] = useState<string | null>(null);

  if (isLoading) return <div className="p-4"><Skeleton className="h-40 w-full rounded-lg" /></div>;
  if (error) return <p className="p-4 text-sm text-muted-foreground">{(error as Error).message}</p>;

  const installed = data?.installed ?? [];
  const history = data?.history ?? [];

  return (
    <div className="space-y-6 overflow-y-auto py-4">
      {/* Equipamentos atualmente instalados */}
      <section className="space-y-3 px-4">
        <h3 className="text-sm font-semibold">Instalados agora ({installed.length})</h3>
        {installed.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-10 text-center">
            <Cpu className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nenhum equipamento instalado neste veículo.</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {installed.map((i) => (
              <div
                key={i.type + i.id}
                className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/5 via-card to-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
              >
                {/* Faixa de destaque superior */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary/70 to-primary/20" />
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-inner">
                      {i.type === 'chip' ? <Cpu className="h-5 w-5" /> : <Package className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0">
                      <Badge className={cn('h-4 border-0 px-1.5 text-[10px] font-medium', stockProductTypeBadgeClass(i.type))}>{KIND[i.type]}</Badge>
                      <div className="truncate text-sm font-semibold tabular-nums">{i.label}</div>
                    </div>
                  </div>
                  <Badge variant="outline" className="h-5 shrink-0 gap-1.5 bg-background/70 px-1.5 text-[10px] uppercase tracking-wider shadow-sm">
                    <StatusDot tone={stockStatusTone(i.status)} />{stockStatusLabel(i.type, i.status)}
                  </Badge>
                </div>
                {/* Matriz 2x2 fixa: N. OS preenche o espaço em branco da grade */}
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  <InfoCell label="IMEI" value={i.tracker} />
                  <InfoCell label="Modelo" value={i.model} />
                  <InfoCell label="ICCID" value={i.chip} />
                  <InfoCell label="Linha" value={i.line} />
                  {/* N. OS que gerou a instalação; clicável quando existe */}
                  {i.service_order_id ? (
                    <button type="button" onClick={() => setOsId(i.service_order_id!)} title="Abrir OS"
                      className="min-w-0 rounded-md bg-primary/10 px-2 py-1.5 text-left transition-colors hover:bg-primary/20">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">N. OS</div>
                      <div className="flex items-center gap-1 text-xs font-semibold tabular-nums text-primary">
                        <span className="truncate">{i.service_order_number}</span><ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      </div>
                    </button>
                  ) : (
                    <div className="min-w-0 rounded-md bg-muted/50 px-2 py-1.5">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">N. OS</div>
                      <div className="text-xs text-muted-foreground">—</div>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-1.5 border-t pt-2 text-xs text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5" /> Instalado em {fmt(i.since)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Histórico: tabela oficial de movimentações */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3 border-b px-4 pb-3">
          <h3 className="text-sm font-semibold">Histórico <span className="font-normal text-muted-foreground">· {history.length} movimentação(ões)</span></h3>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => refetch()} disabled={isFetching} title="Atualizar movimentações" aria-label="Atualizar movimentações">
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
        <div className="overflow-x-auto">
          {/* showDirection: seta verde (entrada) / vermelha (saída) em relação a este veículo */}
          {/* v1.4.0 — mesma ordem da aba da O.S. (BR-EST-061): no empate de horário, a instalação fica por cima */}
          <StockMovementsTable items={sortMovementsForDisplay(history)} isLoading={false} compact showDirection />
        </div>
      </section>
      <ServiceOrderDetailsModal open={!!osId} onOpenChange={(o) => !o && setOsId(null)} serviceOrderId={osId} />
    </div>
  );
}
